/**
 * Pedidos de la tienda sobre ResourceIndex. La fila abre la ficha /store/orders/:id.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { AdminConfirmDialog } from '@/components/shared/AdminConfirmDialog';
import { ResourceIndex, type ResourceIndexColumn } from '@/components/shared/ResourceIndex';
import {
    parseResourceIndexQuery,
    selectPageIds,
    toggleSelectedId,
    writeResourceIndexQuery,
    type ResourceIndexQuery,
} from '@/components/shared/resourceIndexState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useAuth } from '@/hooks/useAuth';
import { esCount } from '@/lib/es-count';
import { formatDate, formatDateTime, formatEur, formatEurFromCents, formatEurInput, formatNumber } from '@/lib/format-es';
import { notifyErr, notifyOk } from '@/lib/notify';
import { rememberStoreOrdersListSearch } from '@/lib/store-orders-return';
import { STATUS_TONE_VARIANT } from '@/lib/store-status';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { useCatalogSuppliers } from '@/pages/store/catalog/hooks/useStoreCatalog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { statusLabel } from './orderStatus';
import {
    CANCELLABLE_STATUSES,
    COMPLETABLE_STATUSES,
    ORDER_CSV_HEADERS,
    ORDER_INDEX_URL,
    ORDER_PAGE_SIZE,
    ORDER_SELECT_CAP,
    ORDER_VIEW_LABELS,
    ORDER_VIEWS,
    centsFromFilter,
    idsWithStatus,
    isIsoDate,
    rowsToCsv,
    waitDays,
    waitLabel,
    waitTone,
} from './orderListModel';
import {
    fetchStoreOrders,
    useCancelSelectedStoreOrders,
    useCompleteSelectedStoreOrders,
    useStoreOrderViewCounts,
    useStoreOrdersList,
} from './hooks/useStoreOrders';
import { StoreOrderStatusBadge } from './components';
import type { StoreOrderListItem } from './types';

type BulkAction = 'complete' | 'cancel' | 'export';

const FILTER_CLASS =
    'h-9 rounded-lg border border-border bg-card px-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50';

function downloadCsv(filename: string, csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}

function originName(key: string, suppliers: { slug: string; name: string }[]): string {
    if (key === 'merch') return 'Merch AiPeces';
    if (key === 'mixed') return 'Mixto';
    return suppliers.find((supplier) => supplier.slug === key)?.name ?? key;
}

function csvCells(row: StoreOrderListItem, now: Date): string[] {
    const days = waitDays(row.paid_at, row.status, row.shipped_at, now);
    return [
        row.order_number,
        formatDateTime(row.created_at),
        `${row.customer_name} ${row.customer_email}`.trim(),
        row.origin_label || '—',
        formatNumber(row.item_count),
        formatEurFromCents(row.total_cents),
        statusLabel(row.status),
        waitLabel(days),
        row.legal_invoice_number ?? '',
    ];
}

export function StoreOrdersPage({ lockedOrigin }: { lockedOrigin?: string } = {}) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const query = useMemo(() => parseResourceIndexQuery(params, ORDER_INDEX_URL), [params]);
    const minCents = centsFromFilter(query.filters.min);

    const listParams = useMemo(
        () => ({
            view: query.view,
            q: query.q,
            page: query.page,
            sort: query.sort,
            dir: query.dir,
            origin: lockedOrigin || query.filters.origin,
            from: query.filters.from,
            to: query.filters.to,
            minCents,
        }),
        [query, minCents, lockedOrigin],
    );

    const list = useStoreOrdersList(listParams);
    const counts = useStoreOrderViewCounts({
        q: listParams.q,
        origin: lockedOrigin || listParams.origin,
        from: listParams.from,
        to: listParams.to,
        minCents: listParams.minCents,
    });
    const suppliers = useCatalogSuppliers();
    const cancelSelected = useCancelSelectedStoreOrders();
    const completeSelected = useCompleteSelectedStoreOrders();

    const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
    const total = list.data?.total ?? 0;

    const [searchDraft, setSearchDraft] = useState(query.q);
    const [prevQ, setPrevQ] = useState(query.q);
    if (query.q !== prevQ) {
        setPrevQ(query.q);
        setSearchDraft(query.q);
    }

    const minFromUrl = minCents == null ? '' : formatEurInput(minCents / 100);
    const [minDraft, setMinDraft] = useState(minFromUrl);
    const [prevMin, setPrevMin] = useState(query.filters.min ?? '');
    if ((query.filters.min ?? '') !== prevMin) {
        setPrevMin(query.filters.min ?? '');
        setMinDraft(minFromUrl);
    }

    const scope = `${query.view}|${query.q}|${query.filters.origin ?? ''}|${query.filters.from ?? ''}|${query.filters.to ?? ''}|${query.filters.min ?? ''}|${query.sort ?? ''}|${query.dir ?? ''}`;
    const [scopeKey, setScopeKey] = useState(scope);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    if (scope !== scopeKey) {
        setScopeKey(scope);
        setSelectedIds([]);
    }

    const [invoiceDownloadingId, setInvoiceDownloadingId] = useState<string | null>(null);
    const [selectingView, setSelectingView] = useState(false);
    const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
    const cacheRef = useRef(new Map<string, StoreOrderListItem>());

    useEffect(() => {
        for (const row of rows) cacheRef.current.set(row.id, row);
    }, [rows]);

    const listSearch = params.toString();
    useEffect(() => {
        if (lockedOrigin) return;
        rememberStoreOrdersListSearch(listSearch ? `?${listSearch}` : '');
    }, [listSearch, lockedOrigin]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            if (searchDraft.trim() === query.q.trim()) return;
            const next = writeResourceIndexQuery(
                params,
                { ...query, q: searchDraft, page: 1 },
                ORDER_INDEX_URL,
            );
            setParams(next);
        }, 300);
        return () => window.clearTimeout(handle);
    }, [searchDraft, query, params, setParams]);

    function commit(next: ResourceIndexQuery) {
        const written = writeResourceIndexQuery(params, next, ORDER_INDEX_URL);
        if (written.toString() !== params.toString()) setParams(written);
    }

    function setFilter(key: string, value: string | undefined) {
        const filters = { ...query.filters };
        if (value) filters[key] = value;
        else delete filters[key];
        commit({ ...query, filters, page: 1 });
    }

    const supplierRows = suppliers.data ?? [];
    const known = new Map(cacheRef.current);
    for (const row of rows) known.set(row.id, row);
    const knownSelected = selectedIds
        .map((id) => known.get(id))
        .filter((row): row is StoreOrderListItem => Boolean(row));
    const completableIds = idsWithStatus(knownSelected, selectedIds, COMPLETABLE_STATUSES);
    const cancellableIds = idsWithStatus(knownSelected, selectedIds, CANCELLABLE_STATUSES);

    const columns = useMemo<ResourceIndexColumn<StoreOrderListItem>[]>(() => {
        return [
            {
                id: 'order_number',
                header: 'Pedido',
                cell: (row) => <span className="font-mono text-xs text-foreground">{row.order_number}</span>,
            },
            {
                id: 'created_at',
                header: 'Fecha',
                cell: (row) => (
                    <span className="whitespace-nowrap text-muted-foreground tabular-nums">
                        {formatDateTime(row.created_at)}
                    </span>
                ),
            },
            {
                id: 'customer_name',
                header: 'Cliente',
                cell: (row) => (
                    <div className="max-w-[220px]">
                        <div className="truncate text-foreground">{row.customer_name}</div>
                        <div className="truncate text-xs text-muted-foreground">{row.customer_email}</div>
                    </div>
                ),
            },
            {
                id: 'origin_label',
                header: 'Origen',
                cell: (row) => <span className="text-foreground">{row.origin_label || '—'}</span>,
            },
            {
                id: 'item_count',
                header: 'Artículos',
                align: 'right',
                cell: (row) => <span className="text-muted-foreground">{formatNumber(row.item_count)}</span>,
            },
            {
                id: 'total_cents',
                header: 'Total',
                align: 'right',
                cell: (row) => <span className="font-medium text-foreground">{formatEurFromCents(row.total_cents)}</span>,
            },
            {
                id: 'status',
                header: 'Estado',
                cell: (row) => <StoreOrderStatusBadge status={row.status} />,
            },
            {
                id: 'wait_days',
                header: 'Espera',
                cell: (row) => {
                    const days = waitDays(row.paid_at, row.status, row.shipped_at, new Date());
                    const tone = waitTone(days);
                    if (!tone) return <span className="text-muted-foreground/40">—</span>;
                    return (
                        <StatusBadge
                            status={tone}
                            label={waitLabel(days)}
                            variant={STATUS_TONE_VARIANT[tone]}
                        />
                    );
                },
            },
            {
                id: 'invoice',
                header: 'Factura',
                cell: (row) =>
                    row.legal_invoice_id ? (
                        <button
                            type="button"
                            title={row.legal_invoice_number ? `Descargar ${row.legal_invoice_number}` : 'Descargar factura'}
                            disabled={invoiceDownloadingId === row.legal_invoice_id}
                            onClick={(event) => {
                                event.stopPropagation();
                                const invoiceId = row.legal_invoice_id!;
                                setInvoiceDownloadingId(invoiceId);
                                downloadLegalInvoicePdf(invoiceId, row.legal_invoice_number ?? 'factura')
                                    .catch((err) => {
                                        notifyErr(err instanceof Error ? err.message : 'Error al descargar la factura');
                                    })
                                    .finally(() => setInvoiceDownloadingId(null));
                            }}
                            className="inline-flex items-center justify-center rounded-lg p-1.5 text-primary hover:bg-primary/10 disabled:opacity-50"
                        >
                            {invoiceDownloadingId === row.legal_invoice_id ? (
                                <Loader2 size={16} className="animate-spin" />
                            ) : (
                                <FileText size={16} />
                            )}
                        </button>
                    ) : (
                        <span className="text-xs text-muted-foreground/40">—</span>
                    ),
            },
        ];
    }, [invoiceDownloadingId]);

    const chips = [
        !lockedOrigin && query.filters.origin
            ? { id: 'origin', label: `Origen: ${originName(query.filters.origin, supplierRows)}` }
            : null,
        query.filters.from && isIsoDate(query.filters.from)
            ? { id: 'from', label: `Desde ${formatDate(query.filters.from)}` }
            : null,
        query.filters.to && isIsoDate(query.filters.to)
            ? { id: 'to', label: `Hasta ${formatDate(query.filters.to)}` }
            : null,
        minCents != null ? { id: 'min', label: `Desde ${formatEur(minCents / 100)}` } : null,
    ].filter((chip): chip is { id: string; label: string } => Boolean(chip));

    const pageIds = rows.map((row) => row.id);
    const dialogCount =
        pendingAction === 'complete'
            ? completableIds.length
            : pendingAction === 'cancel'
              ? cancellableIds.length
              : selectedIds.length;

    function confirmAction() {
        if (pendingAction === 'export') {
            const now = new Date();
            const exported = selectedIds
                .map((id) => known.get(id))
                .filter((row): row is StoreOrderListItem => Boolean(row));
            downloadCsv('pedidos.csv', rowsToCsv(ORDER_CSV_HEADERS, exported.map((row) => csvCells(row, now))));
            setPendingAction(null);
            notifyOk(`Exportados ${esCount(exported.length, 'pedido')}`);
            return;
        }
        const ids = pendingAction === 'complete' ? completableIds : cancellableIds;
        const mutation = pendingAction === 'complete' ? completeSelected : cancelSelected;
        mutation.mutate(ids, {
            onSuccess: (count) => {
                setPendingAction(null);
                setSelectedIds([]);
                notifyOk(
                    pendingAction === 'complete'
                        ? `Marcados ${esCount(count, 'pedido')} como completados`
                        : `Cancelados ${esCount(count, 'pedido')}`,
                );
            },
            onError: (err) => {
                notifyErr(err instanceof Error ? err.message : 'No se ha podido aplicar la acción');
            },
        });
    }

    async function selectEntireView() {
        if (!user?.id || total <= 0) return;
        setSelectingView(true);
        try {
            const fetched = await fetchStoreOrders(user.id, {
                ...listParams,
                limit: Math.min(total, ORDER_SELECT_CAP),
                offset: 0,
            });
            for (const row of fetched.rows) cacheRef.current.set(row.id, row);
            setSelectedIds(fetched.rows.map((row) => row.id));
            if (fetched.total > fetched.rows.length) {
                notifyOk(`Se han seleccionado los primeros ${formatNumber(fetched.rows.length)} de esta vista`);
            }
        } catch (err) {
            notifyErr(err instanceof Error ? err.message : 'No se ha podido seleccionar la vista');
        } finally {
            setSelectingView(false);
        }
    }

    return (
        <div className="space-y-6">
            <ResourceIndex
                actions={
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                void list.refetch();
                                void counts.refetch();
                            }}
                            disabled={list.isFetching}
                            className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={list.isFetching ? 'animate-spin' : ''} />
                            Actualizar
                        </button>
                    </>
                }
                views={ORDER_VIEWS.map((id) => ({
                    id,
                    label: ORDER_VIEW_LABELS[id],
                    count: counts.data?.[id],
                }))}
                view={query.view}
                onViewChange={(id) => commit({ ...query, view: id, page: 1, sort: null, dir: null })}
                search={searchDraft}
                onSearchChange={setSearchDraft}
                searchPlaceholder="Buscar nº pedido, email, nombre…"
                filters={
                    <>
                        {lockedOrigin ? null : (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button type="button" variant="outline" size="sm">
                                    {query.filters.origin
                                        ? originName(query.filters.origin, supplierRows)
                                        : 'Origen'}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="max-h-72 overflow-y-auto">
                                <DropdownMenuItem onSelect={() => setFilter('origin', undefined)}>
                                    Todos los orígenes
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setFilter('origin', 'merch')}>
                                    Merch AiPeces
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setFilter('origin', 'mixed')}>
                                    Mixto
                                </DropdownMenuItem>
                                {supplierRows.map((supplier) => (
                                    <DropdownMenuItem
                                        key={supplier.slug}
                                        onSelect={() => setFilter('origin', supplier.slug)}
                                    >
                                        {supplier.name}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                        <input
                            type="date"
                            aria-label="Desde"
                            value={isIsoDate(query.filters.from ?? '') ? query.filters.from : ''}
                            onChange={(event) => setFilter('from', event.target.value || undefined)}
                            className={FILTER_CLASS}
                        />
                        <input
                            type="date"
                            aria-label="Hasta"
                            value={isIsoDate(query.filters.to ?? '') ? query.filters.to : ''}
                            onChange={(event) => setFilter('to', event.target.value || undefined)}
                            className={FILTER_CLASS}
                        />
                        <input
                            type="text"
                            inputMode="decimal"
                            aria-label="Importe mínimo"
                            placeholder="Importe mín. €"
                            value={minDraft}
                            onChange={(event) => setMinDraft(event.target.value)}
                            onBlur={() => {
                                const trimmed = minDraft.trim();
                                if (!trimmed) {
                                    setFilter('min', undefined);
                                    return;
                                }
                                const amount = Number(trimmed.replace(',', '.'));
                                if (!Number.isFinite(amount) || amount < 0) {
                                    setMinDraft(minFromUrl);
                                    return;
                                }
                                setFilter('min', String(Math.round(amount * 100)));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') event.currentTarget.blur();
                            }}
                            className={`${FILTER_CLASS} w-36`}
                        />
                    </>
                }
                chips={chips}
                onRemoveChip={(id) => setFilter(id, undefined)}
                columns={columns}
                rows={rows}
                rowId={(row) => row.id}
                sort={query.sort}
                sortDir={query.dir === 'desc' ? 'desc' : 'asc'}
                onSort={(columnId) => {
                    const dir = query.sort === columnId && query.dir !== 'desc' ? 'desc' : 'asc';
                    commit({ ...query, sort: columnId, dir, page: 1 });
                }}
                selectedIds={selectedIds}
                onToggleRow={(id) => setSelectedIds((current) => toggleSelectedId(current, id))}
                onTogglePage={(checked) => setSelectedIds((current) => selectPageIds(current, pageIds, checked))}
                onSelectView={() => {
                    void selectEntireView();
                }}
                selectViewPending={selectingView}
                bulkActions={[
                    {
                        id: 'complete',
                        label: 'Marcar como completados',
                        disabled: completableIds.length === 0 || completeSelected.isPending,
                        disabledReason: completeSelected.isPending
                            ? 'Aplicando…'
                            : 'Solo para pedidos pagados, en preparación o enviados',
                        onClick: () => setPendingAction('complete'),
                    },
                    {
                        id: 'cancel',
                        label: 'Cancelar',
                        disabled: cancellableIds.length === 0 || cancelSelected.isPending,
                        disabledReason: cancelSelected.isPending
                            ? 'Aplicando…'
                            : 'Solo para pendientes de pago y checkouts abandonados',
                        onClick: () => setPendingAction('cancel'),
                    },
                    {
                        id: 'export',
                        label: 'Exportar CSV',
                        disabled: knownSelected.length === 0,
                        disabledReason: 'Los pedidos seleccionados no están cargados en esta página',
                        onClick: () => setPendingAction('export'),
                    },
                ]}
                page={query.page}
                pageSize={ORDER_PAGE_SIZE}
                total={total}
                onPageChange={(page) => commit({ ...query, page })}
                isLoading={list.isLoading}
                error={list.error instanceof Error ? list.error.message : null}
                emptyTitle={list.isError ? 'No se ha podido cargar el listado' : 'No hay pedidos en esta vista'}
                emptyDescription={list.isError ? undefined : 'Prueba con otra vista o quita algún filtro.'}
                onRowClick={(row) => {
                    if (lockedOrigin) {
                        navigate(`/store/orders/${row.id}`, {
                            state: { listSearch: `/store/suppliers/${lockedOrigin}?sub=pedidos` },
                        });
                        return;
                    }
                    const search = listSearch ? `?${listSearch}` : '';
                    rememberStoreOrdersListSearch(search);
                    navigate(`/store/orders/${row.id}`, { state: { listSearch: search } });
                }}
            />

            <AdminConfirmDialog
                open={pendingAction != null}
                title={
                    pendingAction === 'complete'
                        ? 'Marcar como completados'
                        : pendingAction === 'cancel'
                          ? 'Cancelar pedidos'
                          : 'Exportar CSV'
                }
                description={
                    pendingAction === 'complete'
                        ? 'Se marcarán como completados los pedidos pagados, en preparación o enviados de la selección. Es distinto de Entregado: no implica entrega física. El resto de la selección no cambia.'
                        : pendingAction === 'cancel'
                          ? 'Se cancelarán los pedidos pendientes de pago de la selección, recientes o abandonados. El resto no cambia.'
                          : 'Se descargará un CSV con las columnas visibles de los pedidos seleccionados.'
                }
                confirmLabel={
                    pendingAction === 'complete'
                        ? 'Marcar como completados'
                        : pendingAction === 'cancel'
                          ? 'Cancelar pedidos'
                          : 'Exportar'
                }
                variant={pendingAction === 'export' ? 'default' : 'danger'}
                affectedCount={dialogCount}
                affectedNoun="pedidos"
                confirmDisabled={
                    dialogCount === 0 || completeSelected.isPending || cancelSelected.isPending
                }
                onConfirm={confirmAction}
                onCancel={() => setPendingAction(null)}
            />
        </div>
    );
}
